import { useEffect, useRef, useState } from 'react';
import type {
  ListadoOrigen,
  ListadoResumen,
  ListadoVigente,
  ResultadoCargue,
} from '@reembolsables/shared';
import { copy } from '../../copy/es';
import { api, ErrorApi, textoDeError } from '../../lib/api';
import { useSesion } from '../sesion';
import { Link } from 'react-router-dom';
import { PeriodoSelect } from '../PeriodoSelect';
import {
  capitalizar,
  fechaHoraLarga,
  nombrePeriodo,
  numero,
  periodoActual,
} from '../../lib/formato';

/** Mockup screen 2: drop zone, result box, context strip and the current listing table (HU-3). */
export function CarguePage() {
  const { usuario } = useSesion();
  const c = copy.admin.cargue;
  const puedeCargar = usuario?.rol === 'operador';
  const [periodo, setPeriodo] = useState(periodoActual());
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoCargue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vigente, setVigente] = useState<ListadoVigente | null>(null);
  const [historico, setHistorico] = useState<ListadoResumen[]>([]);
  const [verTodos, setVerTodos] = useState(false);
  const [origen, setOrigen] = useState<ListadoOrigen | null>(null);
  const inputArchivo = useRef<HTMLInputElement>(null);

  async function cargarDatos() {
    try {
      setVigente(await api<ListadoVigente>('/api/admin/listados/vigente'));
    } catch (e) {
      if (e instanceof ErrorApi && e.estado === 404) setVigente(null);
      else setError(textoDeError(e));
    }
    try {
      setHistorico(await api<ListadoResumen[]>('/api/admin/listados'));
    } catch (e) {
      setError(textoDeError(e));
    }
    try {
      setOrigen(await api<ListadoOrigen>('/api/admin/listados/origen'));
    } catch {
      setOrigen(null);
    }
  }
  useEffect(() => {
    void cargarDatos();
  }, []);

  async function subir(archivo: File) {
    setError(null);
    setResultado(null);
    setEnviando(true);
    const form = new FormData();
    form.append('periodo', periodo);
    form.append('archivo', archivo);
    try {
      setResultado(
        await api<ResultadoCargue>('/api/admin/listados', { method: 'POST', body: form }),
      );
      await cargarDatos();
    } catch (err) {
      if (err instanceof ErrorApi && err.estado === 422)
        setResultado(err.cuerpo as ResultadoCargue);
      else setError(textoDeError(err));
    } finally {
      setEnviando(false);
      if (inputArchivo.current) inputArchivo.current.value = '';
    }
  }

  const ultimo = historico[0];
  const filas = vigente ? (verTodos ? vigente.centros : vigente.centros.slice(0, 5)) : [];

  return (
    <div>
      <h2>{c.titulo}</h2>
      <p className="sub">{c.subtitulo}</p>
      {origen?.origen === 'drive' && (
        <div className="ok-box" role="note">
          {c.origenDrive}
        </div>
      )}

      {puedeCargar && (
        <>
          <PeriodoSelect id="periodo" etiqueta={c.periodo} valor={periodo} onChange={setPeriodo} />
          <div
            className="drop"
            role="button"
            tabIndex={0}
            onClick={() => inputArchivo.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && inputArchivo.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) void subir(f);
            }}
          >
            <div className="big">{enviando ? copy.comun.guardando : c.dropTitulo}</div>
            <div className="sm2">{c.dropNota}</div>
            <input
              ref={inputArchivo}
              type="file"
              accept=".xlsx,.csv"
              aria-label={c.archivo}
              hidden
              onChange={(e) => e.target.files?.[0] && void subir(e.target.files[0])}
            />
          </div>
        </>
      )}
      {!puedeCargar && <p className="sub">{c.soloOperador}</p>}

      {error && (
        <div className="ok-box bad" role="alert">
          {error}
        </div>
      )}
      {resultado && (
        <div className={`ok-box ${resultado.resultado === 'aceptado' ? '' : 'bad'}`} role="status">
          <b>{resultado.resultado === 'aceptado' ? c.aceptado : c.rechazado}</b> ·{' '}
          {capitalizar(nombrePeriodo(resultado.periodo))}
          {resultado.resultado === 'aceptado' && (
            <> · {c.resumen(resultado.centros_costo, resultado.destinatarios)}</>
          )}
          {resultado.errores.map((e, i) => (
            <div key={i}>{e.mensaje}</div>
          ))}
          {resultado.advertencias.map((a, i) => (
            <div key={i} style={{ color: '#8a4f0d' }}>
              {a.mensaje}
            </div>
          ))}
        </div>
      )}

      {vigente ? (
        <>
          <div className="ctx">
            <div>
              <div className="k">{c.ctx.vigente}</div>
              <div className="v">{capitalizar(nombrePeriodo(vigente.periodo))}</div>
            </div>
            <div>
              <div className="k">{c.ctx.centros}</div>
              <div className="v">{numero(vigente.centros.length)}</div>
            </div>
            <div>
              <div className="k">{c.ctx.correos}</div>
              <div className="v">{numero(vigente.destinatarios)}</div>
            </div>
            <div>
              <div className="k">{c.ctx.ultima}</div>
              <div className="v">{fechaHoraLarga(vigente.cargadoEn)}</div>
            </div>
            {ultimo && (
              <div>
                <div className="k">{c.ctx.cargadoPor}</div>
                <div className="v">{ultimo.cargadoPor}</div>
              </div>
            )}
          </div>
          <table className="mk">
            <thead>
              <tr>
                <th>{c.col.centro}</th>
                <th>{c.col.proyecto}</th>
                <th>{c.col.tercero}</th>
                <th>{c.col.correo}</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.codigo}>
                  <td>
                    <b>{r.codigo}</b>
                  </td>
                  <td>{r.nombreProyecto}</td>
                  <td>{r.tercero}</td>
                  <td>{r.correos.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hist">
            {c.mostrando(filas.length, vigente.centros.length)}
            {vigente.centros.length > 5 && (
              <>
                {' '}
                ·{' '}
                <button onClick={() => setVerTodos((v) => !v)}>
                  {verTodos ? c.verMenos : c.verTodos}
                </button>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="ctx">
          <div>
            <div className="k">{c.ctx.vigente}</div>
            <div className="v">{c.sinVigente}</div>
          </div>
        </div>
      )}

      {vigente && puedeCargar && (
        <div style={{ marginTop: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link className="cta amber" to="/admin/publicacion">
            {c.enviarAccesos}
          </Link>
          <Link className="ghost" to="/admin/entregas">
            {c.verEntregas}
          </Link>
        </div>
      )}

      {historico.length > 0 && (
        <>
          <div className="seccion-lbl">{c.historicoTitulo.toUpperCase()}</div>
          <table className="mk">
            <thead>
              <tr>
                <th>{c.col.fecha}</th>
                <th>{c.col.periodo}</th>
                <th>{c.col.archivo}</th>
                <th>{c.col.origen}</th>
                <th>{c.col.estado}</th>
                <th>{c.col.usuario}</th>
              </tr>
            </thead>
            <tbody>
              {historico.slice(0, 8).map((h) => (
                <tr key={h.id}>
                  <td>{fechaHoraLarga(h.cargadoEn)}</td>
                  <td>{h.periodo}</td>
                  <td>{h.archivoNombre}</td>
                  <td>
                    {c.origen[h.origen]}
                    {h.archivoModificadoPor ? ` · ${h.archivoModificadoPor}` : ''}
                  </td>
                  <td>
                    <span
                      className={`pill ${h.estado === 'vigente' ? 'p-ok' : h.estado === 'rechazado' ? 'p-bad' : 'p-no'}`}
                    >
                      {c.estado[h.estado]}
                    </span>
                  </td>
                  <td>{h.cargadoPor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
